import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Plus, X, Lock } from 'lucide-react';
import { messagesApi, type CommsTeam, type ConversationDetail } from '../../lib/comms';
import { FormError } from '../forms/common';

const ALL: Array<{ team: CommsTeam; label: string }> = [
  { team: 'PROPERTY', label: 'Property manager' }, { team: 'BUILDING', label: 'Building manager' },
  { team: 'LETTINGS', label: 'Lettings' }, { team: 'ACCOUNTS', label: 'Accounts' },
];

/**
 * "Who is on this" (prototype §27a): the team the resident wrote to, plus any brought in. Staff on the thread bring
 * another team in or take one off; each change is noted in the thread for staff only.
 */
export function TeamsStrip({ c, colour, onChanged }: { c: ConversationDetail; colour: Record<CommsTeam, string>; onChanged: (d: ConversationDetail) => void }) {
  const [confirm, setConfirm] = useState<CommsTeam | null>(null);
  const on = new Map(c.teams.map((t) => [t.team, t]));
  const act = useMutation({
    mutationFn: ({ team, add }: { team: CommsTeam; add: boolean }) => (add ? messagesApi.addTeam(c.id, team) : messagesApi.dropTeam(c.id, team)),
    onSuccess: (d) => { setConfirm(null); if (d && (d as ConversationDetail).messages) onChanged(d); },
  });
  const can = c.viewer.canTeams;

  return (
    <div className="border-b border-ink-100 bg-white px-4 py-2.5 sm:px-5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">Who is on this</span>
        {ALL.map(({ team, label }) => {
          const t = on.get(team);
          const fixed = t?.original;
          if (!t && !can) return null;
          return (
            <button key={team} disabled={!can || fixed || act.isPending}
              onClick={() => (t ? setConfirm(team) : act.mutate({ team, add: true }))}
              title={fixed ? 'The resident wrote to this team — they stay on it' : t ? `Brought in${t.addedByName ? ` by ${t.addedByName}` : ''} — click to take them off` : `Bring ${label} into this conversation`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-[3px] text-[11.5px] font-medium transition ${t ? 'text-on-primary' : 'border-ink-200 text-ink-500 hover:border-ink-500 hover:text-ink-800'} disabled:cursor-default`}
              style={t ? { background: colour[team], borderColor: colour[team] } : undefined}>
              {label}{t ? (fixed ? null : can && <X className="h-3 w-3" />) : <Plus className="h-3 w-3" />}
            </button>
          );
        })}
      </div>
      {can && <p className="mt-1 flex items-center gap-1 text-[11px] text-ink-400"><Lock className="h-3 w-3" />A team brought in sees the whole conversation, internal notes included. The resident is not told.</p>}
      {confirm && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          <span className="flex-1">Take {ALL.find((x) => x.team === confirm)?.label} off this? They stop seeing the conversation. It is noted in the thread.</span>
          <button onClick={() => setConfirm(null)} className="rounded-lg px-2 py-1 text-ink-600 hover:bg-white">Keep them</button>
          <button onClick={() => act.mutate({ team: confirm, add: false })} disabled={act.isPending} className="rounded-lg bg-amber-600 px-2.5 py-1 font-medium text-on-primary">Take off</button>
        </div>
      )}
      {act.error && <div className="mt-2"><FormError error={act.error} /></div>}
    </div>
  );
}
